export const NODE_PATH_DEFAULT = {
  NODE: {
    EMPTY: "",
    UV: "/COP/imageUv",
    ENV_MAP: "/COP/envMap",
    CUBE_MAP: "/COP/cubeCamera"
  }
};
export class TypedNodePathParamValue {
  constructor(_path = "") {
    this._path = _path;
    this._node = null;
  }
  setPath(path) {
    this._path = path;
  }
  setNode(node) {
    this._node = node;
  }
  path() {
    return this._path;
  }
  node() {
    return this._node;
  }
  resolve(nodeStart) {
    this._node = CoreWalker.findNode(nodeStart, this._path);
  }
  clone() {
    const cloned = new TypedNodePathParamValue(this._path);
    cloned.setNode(this._node);
    return cloned;
  }
  nodeWithContext(context, errorState) {
    const found_node = this.node();
    if (!found_node) {
      errorState == null ? void 0 : errorState.set(`no node found at ${this.path()}`);
      return;
    }
    const node_context = found_node.context();
    if (node_context == context) {
      return found_node;
    } else {
      errorState == null ? void 0 : errorState.set(`expected ${context} node, but got a ${node_context}`);
      return;
    }
  }
}
export class TypedParamPathParamValue {
  constructor(_path = "") {
    this._path = _path;
    this._param = null;
  }
  setPath(path) {
    this._path = path;
  }
  setParam(param) {
    this._param = param;
  }
  path() {
    return this._path;
  }
  param() {
    return this._param;
  }
  resolve(nodeStart) {
    this._param = CoreWalker.findParam(nodeStart, this._path);
  }
  clone() {
    const cloned = new TypedParamPathParamValue(this._path);
    cloned.setParam(this._param);
    return cloned;
  }
  paramWithType(paramType, error_state) {
    const foundParam = this.param();
    if (!foundParam) {
      error_state == null ? void 0 : error_state.set(`no param found at ${this.path()}`);
      return;
    }
    if (foundParam.type() == paramType) {
      return foundParam;
    } else {
      error_state == null ? void 0 : error_state.set(`expected ${paramType} node, but got a ${foundParam.type()}`);
      return;
    }
  }
}
const _CoreWalker = class {
  static splitParentChild(path) {
    const elements = path.split(_CoreWalker.SEPARATOR).filter((e) => e.length > 0);
    const child_path = elements.pop();
    const parent_path = elements.join(_CoreWalker.SEPARATOR);
    return { parent: parent_path, child: child_path };
  }
  static findNode(node_src, path, decomposedPath) {
    if (!node_src) {
      return null;
    }
    const elements = path.split(_CoreWalker.SEPARATOR).filter((e) => e.length > 0);
    const first_element = elements[0];
    let next_node = null;
    if (path[0] === _CoreWalker.SEPARATOR) {
      const path_from_root = path.substring(1);
      next_node = this.findNode(node_src.root(), path_from_root, decomposedPath);
    } else {
      switch (first_element) {
        case _CoreWalker.PARENT:
          decomposedPath == null ? void 0 : decomposedPath.add_path_element(first_element);
          next_node = node_src.parent();
          break;
        case _CoreWalker.CURRENT:
          decomposedPath == null ? void 0 : decomposedPath.add_path_element(first_element);
          next_node = node_src;
          break;
        default:
          next_node = node_src.node(first_element);
          if (next_node) {
            decomposedPath == null ? void 0 : decomposedPath.add_node(first_element, next_node);
          }
      }
      if (next_node != null && elements.length > 1) {
        const remainder = elements.slice(1).join(_CoreWalker.SEPARATOR);
        next_node = this.findNode(next_node, remainder, decomposedPath);
      }
      return next_node;
    }
    return next_node;
  }
  static findParam(node_src, path, decomposedPath) {
    if (!node_src) {
      return null;
    }
    const elements = path.split(_CoreWalker.SEPARATOR);
    if (elements.length === 1) {
      return node_src.params.get(elements[0]);
    } else {
      let node = null;
      if (path[0] === _CoreWalker.SEPARATOR && elements.length == 2) {
        node = node_src.root();
      } else {
        const node_path = elements.slice(0, +(elements.length - 2) + 1 || void 0).join(_CoreWalker.SEPARATOR);
        node = this.findNode(node_src, node_path, decomposedPath);
      }
      if (node != null) {
        const param_name = elements[elements.length - 1];
        const param = node.params.get(param_name);
        if (decomposedPath && param) {
          decomposedPath.add_node(param_name, param);
        }
        return param;
      } else {
        return null;
      }
    }
  }
  static relativePath(src_graph_node, dest_graph_node) {
    const parent = this.closestCommonParent(src_graph_node, dest_graph_node);
    if (!parent) {
      return dest_graph_node.path();
    } else {
      const distance = this.distanceToParent(src_graph_node, parent);
      let up = "";
      if (distance > 0) {
        let i = 0;
        const ups = [];
        while (i++ < distance) {
          ups.push(_CoreWalker.PARENT);
        }
        up = ups.join(_CoreWalker.SEPARATOR) + _CoreWalker.SEPARATOR;
      }
      const parent_path_elements = parent.path().split(_CoreWalker.SEPARATOR).filter((e) => e.length > 0);
      const dest_path_elements = dest_graph_node.path().split(_CoreWalker.SEPARATOR).filter((e) => e.length > 0);
      const remaining_elements = [];
      let cmptr = 0;
      for (let dest_path_element of dest_path_elements) {
        if (!parent_path_elements[cmptr]) {
          remaining_elements.push(dest_path_element);
        }
        cmptr++;
      }
      const down = remaining_elements.join(_CoreWalker.SEPARATOR);
      return this.sanitizePath(`${up}${down}`);
    }
  }
  static sanitizePath(path) {
    return path.replace(/\/\//g, "/");
  }
  static closestCommonParent(graph_node1, graph_node2) {
    const parents1 = this.parents(graph_node1).reverse().concat([graph_node1]);
    const parents2 = this.parents(graph_node2).reverse().concat([graph_node2]);
    const min_depth = Math.min(parents1.length, parents2.length);
    let found_parent = null;
    for (let i = 0; i < min_depth; i++) {
      if (parents1[i].graphNodeId() == parents2[i].graphNodeId()) {
        found_parent = parents1[i];
      }
    }
    return found_parent;
  }
  static parents(graph_node) {
    const parents = [];
    let parent = graph_node.parent();
    while (parent) {
      parents.push(parent);
      parent = parent.parent();
    }
    return parents;
  }
  static distanceToParent(graph_node, dest) {
    let distance = 0;
    let current = graph_node;
    const dest_id = dest.graphNodeId();
    while (current && current.graphNodeId() != dest_id) {
      distance += 1;
      current = current.parent();
    }
    if (current && current.graphNodeId() == dest_id) {
      return distance;
    } else {
      return -1;
    }
  }
  static makeAbsolutePath(nodeSrc, path) {
    if (path[0] == _CoreWalker.SEPARATOR) {
      return path;
    }
    const pathElements = path.split(_CoreWalker.SEPARATOR);
    const firstElement = pathElements.shift();
    if (firstElement) {
      switch (firstElement) {
        case "..": {
          const parent = nodeSrc.parent();
          if (parent) {
            if (parent == nodeSrc.scene().root()) {
              return _CoreWalker.SEPARATOR + pathElements.join(_CoreWalker.SEPARATOR);
            } else {
              return this.makeAbsolutePath(parent, pathElements.join(_CoreWalker.SEPARATOR));
            }
          } else {
            return null;
          }
        }
        case ".": {
          return this.makeAbsolutePath(nodeSrc, pathElements.join(_CoreWalker.SEPARATOR));
        }
        default: {
          return [nodeSrc.path(), path].join(_CoreWalker.SEPARATOR);
        }
      }
    } else {
      return nodeSrc.path();
    }
  }
};
export let CoreWalker = _CoreWalker;
CoreWalker.SEPARATOR = "/";
CoreWalker.DOT = ".";
CoreWalker.CURRENT = _CoreWalker.DOT;
CoreWalker.PARENT = "..";
CoreWalker.CURRENT_WITH_SLASH = `${_CoreWalker.CURRENT}/`;
CoreWalker.PARENT_WITH_SLASH = `${_CoreWalker.PARENT}/`;
CoreWalker.NON_LETTER_PREFIXES = [_CoreWalker.SEPARATOR, _CoreWalker.DOT];
